import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  getAccountMappingSuggestionsURL,
  getAccountMappingsURL,
  saveAccountMappingURL,
  bulkSaveAccountMappingsURL,
  deleteAccountMappingURL,
  getItemMappingSuggestionsURL,
  bulkSaveItemMappingsURL,
  deleteItemMappingURL,
  createAndMapItemURL,
  getPaymentChannelMappingSuggestionsURL,
  bulkSavePaymentChannelMappingsURL,
  deletePaymentChannelMappingURL,
} from "../../../utils/urls";

// ─── shared option lists (not returned by any GET endpoint) ──────────────────
const ACCOUNT_ROLE_OPTIONS = [
  { value: "receivable", label: "Receivable" },
  { value: "liability_deposit", label: "Liability (Deposit)" },
  { value: "liability_deferred_revenue", label: "Liability (Deferred Revenue)" },
  { value: "revenue", label: "Revenue" },
  { value: "cash", label: "Cash" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "None" },
  { value: "rent_lease", label: "Rent" },
  { value: "security_deposit", label: "Security Deposit" },
  { value: "service_charge", label: "Service Charge" },
  { value: "utility_power", label: "Power" },
  { value: "utility_water", label: "Water" },
  { value: "levy_contract", label: "Levy" },
  { value: "property_management", label: "Property Management" },
];

// ─── small shared bits ────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    mapped: { cls: "bg-success", label: "Mapped", icon: "ti-check" },
    suggested: { cls: "bg-warning text-dark", label: "Suggested", icon: "ti-bulb" },
    unmatched: { cls: "bg-light text-dark", label: "Unmatched", icon: "ti-question-mark" },
    unmapped: { cls: "bg-light text-dark", label: "Unmapped", icon: "ti-minus" },
  };
  const cfg = map[status] || map.unmatched;
  return (
    <span className={`badge ${cfg.cls}`}>
      <i className={`ti ${cfg.icon} me-1`}></i>
      {cfg.label}
    </span>
  );
};

const ConfidenceBadge = ({ confidence }) => {
  if (confidence === undefined || confidence === null) return null;
  const pct = Math.round(
    typeof confidence === "number" && confidence <= 1 ? confidence * 100 : confidence,
  );
  return (
    <span className="badge bg-info-subtle text-info ms-1" title="Suggestion confidence">
      {pct}%
    </span>
  );
};

const LoadingBlock = () => (
  <div className="text-center py-5">
    <div className="spinner-border spinner-border-sm" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
    <p className="text-muted small mt-2 mb-0">Fetching live data from QuickBooks…</p>
  </div>
);

const ErrorBlock = ({ error, onRetry }) => {
  const message = typeof error === "string" ? error : "Failed to load data";
  const looksLikeAuthIssue = /token|unauthoriz|not connected|reconnect|expired/i.test(message);
  return (
    <div className="alert alert-danger" role="alert">
      <div className="d-flex align-items-start">
        <i className="ti ti-alert-triangle me-2 mt-1"></i>
        <div className="flex-grow-1">
          <p className="mb-1 fw-medium">Couldn't load data from QuickBooks</p>
          <p className="mb-2 small">{message}</p>
          {looksLikeAuthIssue && (
            <p className="mb-2 small text-muted">
              This usually means the QuickBooks connection needs to be reconnected. Go back to
              "Manage QuickBooks Connections" and reconnect this realm, then try again.
            </p>
          )}
          <button className="btn btn-sm btn-outline-danger" onClick={onRetry}>
            <i className="ti ti-refresh me-1"></i>Retry
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Chart of Accounts tab ─────────────────────────────────────────────────────
const ChartOfAccountsTab = ({ facilityId, qbConfigId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qbAccounts, setQbAccounts] = useState([]);
  const [rows, setRows] = useState([]); // working copy: payserve accounts + local edits
  const [dirty, setDirty] = useState({}); // payserveAccountId -> true
  const [rowErrors, setRowErrors] = useState({}); // payserveAccountId -> error string
  const [savingRow, setSavingRow] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [suggestionsRes, mappingsRes] = await Promise.all([
        makeRequest2(getAccountMappingSuggestionsURL(facilityId, qbConfigId), "GET"),
        makeRequest2(getAccountMappingsURL(facilityId, qbConfigId), "GET"),
      ]);

      if (!suggestionsRes?.success || !suggestionsRes.data?.success) {
        throw new Error(
          suggestionsRes?.data?.error || suggestionsRes?.error || "Failed to load account suggestions",
        );
      }

      const { payserveAccounts = [], qbAccounts: liveQbAccounts = [] } =
        suggestionsRes.data.data || {};

      const savedMappingsByAccountId = {};
      if (mappingsRes?.success && mappingsRes.data?.success) {
        (mappingsRes.data.data?.mappings || []).forEach((m) => {
          savedMappingsByAccountId[m.payserveAccountId] = m;
        });
      }

      const builtRows = payserveAccounts.map((acc) => {
        const saved = savedMappingsByAccountId[acc.payserveAccountId];
        const initialQbAccountId =
          acc.mapping?.qbAccountId || acc.suggestion?.qbAccountId || "";
        return {
          ...acc,
          qbAccountId: initialQbAccountId,
          accountRole: saved?.accountRole || "",
          category: saved?.category || "",
        };
      });

      setQbAccounts(liveQbAccounts);
      setRows(builtRows);
      setDirty({});
      setRowErrors({});
    } catch (err) {
      console.error("Error loading account mapping suggestions:", err);
      setError(err.message || "Failed to load account mapping suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (facilityId && qbConfigId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, qbConfigId]);

  const qbAccountOptions = useMemo(
    () =>
      qbAccounts.map((a) => ({
        value: a.qbAccountId,
        label: `${a.fullyQualifiedName}${a.accountType ? ` (${a.accountType})` : ""}`,
      })),
    [qbAccounts],
  );

  const updateRow = (payserveAccountId, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.payserveAccountId === payserveAccountId ? { ...r, ...patch } : r)),
    );
    setDirty((prev) => ({ ...prev, [payserveAccountId]: true }));
    setRowErrors((prev) => ({ ...prev, [payserveAccountId]: undefined }));
  };

  const saveRow = async (row) => {
    if (!row.qbAccountId) {
      toastify("error", "Select a QuickBooks account before saving");
      return;
    }
    if (!row.accountRole) {
      toastify("error", "Select an account role before saving");
      return;
    }
    setSavingRow(row.payserveAccountId);
    try {
      const response = await makeRequest2(saveAccountMappingURL(facilityId), "POST", {
        qbConfigId,
        payserveAccountId: row.payserveAccountId,
        qbAccountId: row.qbAccountId,
        accountRole: row.accountRole,
        category: row.category || undefined,
      });

      if (response?.success && response.data?.success) {
        toastify("success", `Mapping saved for ${row.payserveAccountName}`);
        setDirty((prev) => {
          const next = { ...prev };
          delete next[row.payserveAccountId];
          return next;
        });
        setRowErrors((prev) => ({ ...prev, [row.payserveAccountId]: undefined }));
        setRows((prev) =>
          prev.map((r) =>
            r.payserveAccountId === row.payserveAccountId
              ? { ...r, status: "mapped" }
              : r,
          ),
        );
      } else {
        const msg = response?.data?.error || response?.error || "Failed to save mapping";
        setRowErrors((prev) => ({ ...prev, [row.payserveAccountId]: msg }));
        toastify("error", msg);
      }
    } catch (err) {
      console.error("Error saving account mapping:", err);
      toastify("error", "Failed to save mapping");
    } finally {
      setSavingRow(null);
    }
  };

  const deleteRow = (row) => {
    confirmDialog({
      message: `Remove the QuickBooks mapping for "${row.payserveAccountName}"?`,
      header: "Confirm Unmap",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      accept: async () => {
        try {
          const response = await makeRequest2(
            deleteAccountMappingURL(facilityId, row.payserveAccountId, qbConfigId),
            "DELETE",
          );
          if (response?.success && response.data?.success) {
            toastify("success", response.data.message || "Mapping removed");
            updateRow(row.payserveAccountId, {
              qbAccountId: "",
              accountRole: "",
              category: "",
              status: "unmatched",
              mapping: null,
            });
            setDirty((prev) => {
              const next = { ...prev };
              delete next[row.payserveAccountId];
              return next;
            });
          } else {
            toastify("error", response?.data?.error || response?.error || "Failed to remove mapping");
          }
        } catch (err) {
          console.error("Error deleting account mapping:", err);
          toastify("error", "Failed to remove mapping");
        }
      },
    });
  };

  const saveAllChanges = async () => {
    const changedRows = rows.filter((r) => dirty[r.payserveAccountId]);
    if (changedRows.length === 0) {
      toastify("info", "No changes to save");
      return;
    }
    const invalid = changedRows.filter((r) => !r.qbAccountId || !r.accountRole);
    if (invalid.length > 0) {
      toastify(
        "error",
        `${invalid.length} row(s) are missing a QuickBooks account or account role. Fix them before saving.`,
      );
      return;
    }

    setBulkSaving(true);
    try {
      const response = await makeRequest2(bulkSaveAccountMappingsURL(facilityId), "POST", {
        qbConfigId,
        mappings: changedRows.map((r) => ({
          payserveAccountId: r.payserveAccountId,
          qbAccountId: r.qbAccountId,
          accountRole: r.accountRole,
          category: r.category || undefined,
        })),
      });

      if (response?.success && response.data) {
        const { message, data } = response.data;
        toastify(response.data.success === false ? "warn" : "success", message || "Bulk save complete");

        const succeededIds = new Set((data?.mappings || []).map((m) => m.payserveAccountId));
        const failedById = {};
        (data?.failed || []).forEach((f) => {
          const id = f.row?.payserveAccountId || f.payserveAccountId;
          if (id) failedById[id] = f.error;
        });

        setDirty((prev) => {
          const next = { ...prev };
          succeededIds.forEach((id) => delete next[id]);
          return next;
        });
        setRowErrors(failedById);
        setRows((prev) =>
          prev.map((r) =>
            succeededIds.has(r.payserveAccountId) ? { ...r, status: "mapped" } : r,
          ),
        );
      } else {
        toastify("error", response?.data?.error || response?.error || "Bulk save failed");
      }
    } catch (err) {
      console.error("Error bulk saving account mappings:", err);
      toastify("error", "Bulk save failed");
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} onRetry={load} />;

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted small mb-0">
          One row per PayServe GL account. Pick the matching QuickBooks account, an account role,
          and (optionally) an invoice category.
        </p>
        <button
          className="btn btn-sm btn-success"
          onClick={saveAllChanges}
          disabled={bulkSaving || dirtyCount === 0}
        >
          {bulkSaving ? (
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          ) : (
            <i className="ti ti-device-floppy me-1"></i>
          )}
          Save All Changes {dirtyCount > 0 ? `(${dirtyCount})` : ""}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="alert alert-light text-center">No PayServe accounts available to map.</div>
      ) : (
        <div className="table-responsive" style={{ maxHeight: "55vh", overflowY: "auto" }}>
          <table className="table table-sm align-middle">
            <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
              <tr>
                <th style={{ minWidth: 180 }}>PayServe Account</th>
                <th style={{ minWidth: 240 }}>QuickBooks Account</th>
                <th style={{ minWidth: 160 }}>Role</th>
                <th style={{ minWidth: 160 }}>Category</th>
                <th style={{ minWidth: 100 }}>Status</th>
                <th className="text-end" style={{ minWidth: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.payserveAccountId}>
                  <td>
                    <div className="fw-medium">{row.payserveAccountName}</div>
                    <small className="text-muted">
                      {row.payserveAccountCode ? `${row.payserveAccountCode} · ` : ""}
                      {row.payserveAccountType || ""}
                    </small>
                  </td>
                  <td>
                    <Dropdown
                      value={row.qbAccountId || null}
                      options={qbAccountOptions}
                      onChange={(e) => updateRow(row.payserveAccountId, { qbAccountId: e.value })}
                      placeholder="Select QuickBooks account"
                      className="w-100"
                      filter
                      showClear
                    />
                    {row.status === "suggested" && row.suggestion && (
                      <div className="mt-1">
                        <small className="text-muted">Suggested: {row.suggestion.qbAccountName}</small>
                        <ConfidenceBadge confidence={row.suggestion.confidence} />
                      </div>
                    )}
                    {rowErrors[row.payserveAccountId] && (
                      <div className="text-danger small mt-1">{rowErrors[row.payserveAccountId]}</div>
                    )}
                  </td>
                  <td>
                    <Dropdown
                      value={row.accountRole || null}
                      options={ACCOUNT_ROLE_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      onChange={(e) => updateRow(row.payserveAccountId, { accountRole: e.value })}
                      placeholder="Select role"
                      className="w-100"
                    />
                  </td>
                  <td>
                    <Dropdown
                      value={row.category || ""}
                      options={CATEGORY_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      onChange={(e) => updateRow(row.payserveAccountId, { category: e.value })}
                      className="w-100"
                    />
                  </td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <button
                        className="btn btn-sm btn-outline-success"
                        title="Save"
                        disabled={savingRow === row.payserveAccountId}
                        onClick={() => saveRow(row)}
                      >
                        {savingRow === row.payserveAccountId ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="ti ti-device-floppy"></i>
                        )}
                      </button>
                      {row.status === "mapped" && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Remove mapping"
                          onClick={() => deleteRow(row)}
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Items tab ─────────────────────────────────────────────────────────────────
const CreateItemForm = ({ category, facilityId, qbConfigId, onCreated, onCancel }) => {
  const [qbAccountId, setQbAccountId] = useState("");
  const [itemName, setItemName] = useState(category.defaultItemName || category.categoryLabel || "");
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const accountOptions = (category.compatibleAccounts || []).map((a) => ({
    value: a.qbAccountId,
    label: `${a.fullyQualifiedName}${a.accountType ? ` (${a.accountType})` : ""}`,
  }));
  const noCompatibleAccounts = accountOptions.length === 0;

  const handleCreate = async () => {
    setValidationError("");
    if (!qbAccountId) {
      const msg = noCompatibleAccounts
        ? `No QuickBooks account with classification "${category.requiredClassification || "Revenue"}" exists in this realm yet - create one in QuickBooks first, then reopen this form.`
        : "Select an income account for the new item.";
      setValidationError(msg);
      toastify("error", msg);
      return;
    }
    if (!itemName.trim()) {
      setValidationError("Enter a name for the new item.");
      toastify("error", "Enter a name for the new item");
      return;
    }
    setSubmitting(true);
    try {
      const response = await makeRequest2(createAndMapItemURL(facilityId), "POST", {
        qbConfigId,
        category: category.category,
        qbAccountId,
        itemName: itemName.trim(),
      });
      if (response?.success && response.data?.success) {
        toastify("success", `Item "${itemName.trim()}" created and mapped`);
        onCreated(response.data.data?.mapping);
      } else {
        const msg = response?.data?.error || response?.error || "Failed to create item";
        setValidationError(msg);
        toastify("error", msg);
      }
    } catch (err) {
      console.error("Error creating and mapping item:", err);
      const msg = err?.message || "Failed to create item";
      setValidationError(msg);
      toastify("error", "Failed to create item");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border rounded p-2 mt-2 bg-light-subtle">
      {noCompatibleAccounts && (
        <div className="alert alert-warning py-1 px-2 small mb-2">
          <i className="ti ti-alert-triangle me-1"></i>
          No QuickBooks account with classification &quot;{category.requiredClassification || "Revenue"}
          &quot; exists in this realm yet. Create one directly in QuickBooks first, then reopen this
          form - it can't create an item without a valid income account to post to.
        </div>
      )}
      <div className="mb-2">
        <label className="form-label small mb-1">Income Account</label>
        <Dropdown
          value={qbAccountId || null}
          options={accountOptions}
          onChange={(e) => setQbAccountId(e.value)}
          placeholder="Select a compatible account"
          className="w-100"
          filter
          disabled={noCompatibleAccounts}
        />
      </div>
      <div className="mb-2">
        <label className="form-label small mb-1">Item Name</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
        />
      </div>
      {validationError && (
        <div className="text-danger small mb-2">
          <i className="ti ti-alert-triangle me-1"></i>
          {validationError}
        </div>
      )}
      <div className="d-flex gap-2">
        <button
          className="btn btn-sm btn-success"
          onClick={handleCreate}
          disabled={submitting || noCompatibleAccounts}
        >
          {submitting ? (
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          ) : (
            <i className="ti ti-plus me-1"></i>
          )}
          Create &amp; Map
        </button>
        <button className="btn btn-sm btn-light" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const ItemsTab = ({ facilityId, qbConfigId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qbItems, setQbItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [savingRow, setSavingRow] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null); // category key currently showing "create item" form

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await makeRequest2(getItemMappingSuggestionsURL(facilityId, qbConfigId), "GET");
      if (!response?.success || !response.data?.success) {
        throw new Error(response?.data?.error || response?.error || "Failed to load item suggestions");
      }
      const { categories = [], qbItems: liveQbItems = [] } = response.data.data || {};
      const builtRows = categories.map((cat) => ({
        ...cat,
        qbItemId: cat.mapping?.qbItemId || cat.suggestion?.qbItemId || "",
      }));
      setQbItems(liveQbItems);
      setRows(builtRows);
      setDirty({});
      setRowErrors({});
    } catch (err) {
      console.error("Error loading item mapping suggestions:", err);
      setError(err.message || "Failed to load item mapping suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (facilityId && qbConfigId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, qbConfigId]);

  const qbItemOptions = useMemo(
    () =>
      qbItems.map((i) => ({
        value: i.qbItemId,
        label: `${i.fullyQualifiedName}${i.incomeAccountName ? ` → ${i.incomeAccountName}` : ""}`,
      })),
    [qbItems],
  );

  const updateRow = (category, patch) => {
    setRows((prev) => prev.map((r) => (r.category === category ? { ...r, ...patch } : r)));
    setDirty((prev) => ({ ...prev, [category]: true }));
    setRowErrors((prev) => ({ ...prev, [category]: undefined }));
  };

  const saveRow = async (row) => {
    if (!row.qbItemId) {
      toastify("error", "Select a QuickBooks item before saving");
      return;
    }
    setSavingRow(row.category);
    try {
      // save_item_mapping.js confirmed to accept { qbConfigId, category, qbItemId }
      const response = await makeRequest2(bulkSaveItemMappingsURL(facilityId), "POST", {
        qbConfigId,
        mappings: [{ category: row.category, qbItemId: row.qbItemId }],
      });
      if (response?.success && response.data) {
        const failed = response.data.data?.failed || [];
        if (failed.length > 0) {
          setRowErrors((prev) => ({ ...prev, [row.category]: failed[0].error }));
          toastify("error", failed[0].error || "Failed to save mapping");
        } else {
          toastify("success", `Mapping saved for ${row.categoryLabel}`);
          setDirty((prev) => {
            const next = { ...prev };
            delete next[row.category];
            return next;
          });
          setRows((prev) =>
            prev.map((r) => (r.category === row.category ? { ...r, status: "mapped" } : r)),
          );
        }
      } else {
        toastify("error", response?.data?.error || response?.error || "Failed to save mapping");
      }
    } catch (err) {
      console.error("Error saving item mapping:", err);
      toastify("error", "Failed to save mapping");
    } finally {
      setSavingRow(null);
    }
  };

  const deleteRow = (row) => {
    confirmDialog({
      message: `Remove the QuickBooks item mapping for "${row.categoryLabel}"?`,
      header: "Confirm Unmap",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      accept: async () => {
        try {
          const response = await makeRequest2(
            deleteItemMappingURL(facilityId, row.category, qbConfigId),
            "DELETE",
          );
          if (response?.success && response.data?.success) {
            toastify("success", response.data.message || "Mapping removed");
            updateRow(row.category, { qbItemId: "", status: "unmatched", mapping: null });
            setDirty((prev) => {
              const next = { ...prev };
              delete next[row.category];
              return next;
            });
          } else {
            toastify("error", response?.data?.error || response?.error || "Failed to remove mapping");
          }
        } catch (err) {
          console.error("Error deleting item mapping:", err);
          toastify("error", "Failed to remove mapping");
        }
      },
    });
  };

  const saveAllChanges = async () => {
    const changedRows = rows.filter((r) => dirty[r.category]);
    if (changedRows.length === 0) {
      toastify("info", "No changes to save");
      return;
    }
    const invalid = changedRows.filter((r) => !r.qbItemId);
    if (invalid.length > 0) {
      toastify("error", `${invalid.length} row(s) are missing a QuickBooks item. Fix them before saving.`);
      return;
    }

    setBulkSaving(true);
    try {
      const response = await makeRequest2(bulkSaveItemMappingsURL(facilityId), "POST", {
        qbConfigId,
        mappings: changedRows.map((r) => ({ category: r.category, qbItemId: r.qbItemId })),
      });

      if (response?.success && response.data) {
        const { message, data } = response.data;
        toastify(response.data.success === false ? "warn" : "success", message || "Bulk save complete");

        const succeeded = new Set((data?.mappings || []).map((m) => m.category));
        const failedByCategory = {};
        (data?.failed || []).forEach((f) => {
          const cat = f.row?.category || f.category;
          if (cat) failedByCategory[cat] = f.error;
        });

        setDirty((prev) => {
          const next = { ...prev };
          succeeded.forEach((c) => delete next[c]);
          return next;
        });
        setRowErrors(failedByCategory);
        setRows((prev) => prev.map((r) => (succeeded.has(r.category) ? { ...r, status: "mapped" } : r)));
      } else {
        toastify("error", response?.data?.error || response?.error || "Bulk save failed");
      }
    } catch (err) {
      console.error("Error bulk saving item mappings:", err);
      toastify("error", "Bulk save failed");
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} onRetry={load} />;

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted small mb-0">
          One row per PayServe invoice category. Pick the matching QuickBooks item, or create a
          new one directly in QuickBooks.
        </p>
        <button
          className="btn btn-sm btn-success"
          onClick={saveAllChanges}
          disabled={bulkSaving || dirtyCount === 0}
        >
          {bulkSaving ? (
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          ) : (
            <i className="ti ti-device-floppy me-1"></i>
          )}
          Save All Changes {dirtyCount > 0 ? `(${dirtyCount})` : ""}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="alert alert-light text-center">No categories available to map.</div>
      ) : (
        <div className="table-responsive" style={{ maxHeight: "55vh", overflowY: "auto" }}>
          <table className="table table-sm align-middle">
            <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
              <tr>
                <th style={{ minWidth: 180 }}>Category</th>
                <th style={{ minWidth: 240 }}>QuickBooks Item</th>
                <th style={{ minWidth: 100 }}>Status</th>
                <th className="text-end" style={{ minWidth: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.category}>
                  <td>
                    <div className="fw-medium">{row.categoryLabel}</div>
                    {row.requiredClassification && (
                      <small className="text-muted">Expects: {row.requiredClassification}</small>
                    )}
                  </td>
                  <td>
                    <Dropdown
                      value={row.qbItemId || null}
                      options={qbItemOptions}
                      onChange={(e) => updateRow(row.category, { qbItemId: e.value })}
                      placeholder="Select QuickBooks item"
                      className="w-100"
                      filter
                      showClear
                    />
                    {row.status === "suggested" && row.suggestion && (
                      <div className="mt-1">
                        <small className="text-muted">Suggested: {row.suggestion.qbItemName}</small>
                        <ConfidenceBadge confidence={row.suggestion.confidence} />
                      </div>
                    )}
                    {rowErrors[row.category] && (
                      <div className="text-danger small mt-1">{rowErrors[row.category]}</div>
                    )}
                    {row.status !== "mapped" && creatingFor === row.category && (
                      <CreateItemForm
                        category={row}
                        facilityId={facilityId}
                        qbConfigId={qbConfigId}
                        onCancel={() => setCreatingFor(null)}
                        onCreated={(mapping) => {
                          setCreatingFor(null);
                          updateRow(row.category, {
                            qbItemId: mapping?.qbItemId || "",
                            status: "mapped",
                          });
                          setDirty((prev) => {
                            const next = { ...prev };
                            delete next[row.category];
                            return next;
                          });
                          load();
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <button
                        className="btn btn-sm btn-outline-success"
                        title="Save"
                        disabled={savingRow === row.category}
                        onClick={() => saveRow(row)}
                      >
                        {savingRow === row.category ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="ti ti-device-floppy"></i>
                        )}
                      </button>
                      {row.status !== "mapped" && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          title="Create new item in QuickBooks"
                          onClick={() => setCreatingFor(creatingFor === row.category ? null : row.category)}
                        >
                          <i className="ti ti-plus"></i>
                        </button>
                      )}
                      {row.status === "mapped" && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Remove mapping"
                          onClick={() => deleteRow(row)}
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Payment Channels tab ───────────────────────────────────────────────────────
const PaymentChannelsTab = ({ facilityId, qbConfigId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qbAccounts, setQbAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [savingRow, setSavingRow] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await makeRequest2(
        getPaymentChannelMappingSuggestionsURL(facilityId, qbConfigId),
        "GET",
      );
      if (!response?.success || !response.data?.success) {
        throw new Error(
          response?.data?.error || response?.error || "Failed to load payment channel suggestions",
        );
      }
      const { channels = [], qbAccounts: liveQbAccounts = [] } = response.data.data || {};
      const builtRows = channels.map((ch) => ({
        ...ch,
        qbAccountId: ch.mapping?.qbAccountId || "",
      }));
      setQbAccounts(liveQbAccounts);
      setRows(builtRows);
      setDirty({});
      setRowErrors({});
    } catch (err) {
      console.error("Error loading payment channel mapping suggestions:", err);
      setError(err.message || "Failed to load payment channel mapping suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (facilityId && qbConfigId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, qbConfigId]);

  const qbAccountOptions = useMemo(
    () =>
      qbAccounts.map((a) => ({
        value: a.qbAccountId,
        label: `${a.fullyQualifiedName}${a.accountType ? ` (${a.accountType})` : ""}`,
      })),
    [qbAccounts],
  );

  const updateRow = (channel, patch) => {
    setRows((prev) => prev.map((r) => (r.channel === channel ? { ...r, ...patch } : r)));
    setDirty((prev) => ({ ...prev, [channel]: true }));
    setRowErrors((prev) => ({ ...prev, [channel]: undefined }));
  };

  const saveRow = async (row) => {
    if (!row.qbAccountId) {
      toastify("error", "Select a QuickBooks account before saving");
      return;
    }
    setSavingRow(row.channel);
    try {
      // save_payment_channel_mapping.js confirmed to accept
      // { qbConfigId, channel, qbAccountId }
      const response = await makeRequest2(bulkSavePaymentChannelMappingsURL(facilityId), "POST", {
        qbConfigId,
        mappings: [{ channel: row.channel, qbAccountId: row.qbAccountId }],
      });
      if (response?.success && response.data) {
        const failed = response.data.data?.failed || [];
        if (failed.length > 0) {
          setRowErrors((prev) => ({ ...prev, [row.channel]: failed[0].error }));
          toastify("error", failed[0].error || "Failed to save mapping");
        } else {
          toastify("success", `Mapping saved for ${row.channelLabel}`);
          setDirty((prev) => {
            const next = { ...prev };
            delete next[row.channel];
            return next;
          });
          setRows((prev) =>
            prev.map((r) => (r.channel === row.channel ? { ...r, status: "mapped" } : r)),
          );
        }
      } else {
        toastify("error", response?.data?.error || response?.error || "Failed to save mapping");
      }
    } catch (err) {
      console.error("Error saving payment channel mapping:", err);
      toastify("error", "Failed to save mapping");
    } finally {
      setSavingRow(null);
    }
  };

  const deleteRow = (row) => {
    confirmDialog({
      message: `Remove the QuickBooks mapping for "${row.channelLabel}"?`,
      header: "Confirm Unmap",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      accept: async () => {
        try {
          const response = await makeRequest2(
            deletePaymentChannelMappingURL(facilityId, row.channel, qbConfigId),
            "DELETE",
          );
          if (response?.success && response.data?.success) {
            toastify("success", response.data.message || "Mapping removed");
            updateRow(row.channel, { qbAccountId: "", status: "unmapped", mapping: null });
            setDirty((prev) => {
              const next = { ...prev };
              delete next[row.channel];
              return next;
            });
          } else {
            toastify("error", response?.data?.error || response?.error || "Failed to remove mapping");
          }
        } catch (err) {
          console.error("Error deleting payment channel mapping:", err);
          toastify("error", "Failed to remove mapping");
        }
      },
    });
  };

  const saveAllChanges = async () => {
    const changedRows = rows.filter((r) => dirty[r.channel]);
    if (changedRows.length === 0) {
      toastify("info", "No changes to save");
      return;
    }
    const invalid = changedRows.filter((r) => !r.qbAccountId);
    if (invalid.length > 0) {
      toastify("error", `${invalid.length} row(s) are missing a QuickBooks account. Fix them before saving.`);
      return;
    }

    setBulkSaving(true);
    try {
      const response = await makeRequest2(bulkSavePaymentChannelMappingsURL(facilityId), "POST", {
        qbConfigId,
        mappings: changedRows.map((r) => ({ channel: r.channel, qbAccountId: r.qbAccountId })),
      });

      if (response?.success && response.data) {
        const { message, data } = response.data;
        toastify(response.data.success === false ? "warn" : "success", message || "Bulk save complete");

        const succeeded = new Set((data?.mappings || []).map((m) => m.channel));
        const failedByChannel = {};
        (data?.failed || []).forEach((f) => {
          const ch = f.row?.channel || f.channel;
          if (ch) failedByChannel[ch] = f.error;
        });

        setDirty((prev) => {
          const next = { ...prev };
          succeeded.forEach((c) => delete next[c]);
          return next;
        });
        setRowErrors(failedByChannel);
        setRows((prev) => prev.map((r) => (succeeded.has(r.channel) ? { ...r, status: "mapped" } : r)));
      } else {
        toastify("error", response?.data?.error || response?.error || "Bulk save failed");
      }
    } catch (err) {
      console.error("Error bulk saving payment channel mappings:", err);
      toastify("error", "Bulk save failed");
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} onRetry={load} />;

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div>
      <div className="alert alert-info small mb-3" role="alert">
        <i className="ti ti-info-circle me-2"></i>
        An unmapped payment channel is valid — its payments simply post to QuickBooks'
        "Undeposited Funds" account by default until mapped.
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted small mb-0">
          One row per PayServe payment channel. Pick the matching QuickBooks bank/asset account.
        </p>
        <button
          className="btn btn-sm btn-success"
          onClick={saveAllChanges}
          disabled={bulkSaving || dirtyCount === 0}
        >
          {bulkSaving ? (
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          ) : (
            <i className="ti ti-device-floppy me-1"></i>
          )}
          Save All Changes {dirtyCount > 0 ? `(${dirtyCount})` : ""}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="alert alert-light text-center">No payment channels available to map.</div>
      ) : (
        <div className="table-responsive" style={{ maxHeight: "50vh", overflowY: "auto" }}>
          <table className="table table-sm align-middle">
            <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
              <tr>
                <th style={{ minWidth: 180 }}>Payment Channel</th>
                <th style={{ minWidth: 240 }}>QuickBooks Account</th>
                <th style={{ minWidth: 100 }}>Status</th>
                <th className="text-end" style={{ minWidth: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.channel}>
                  <td className="fw-medium">{row.channelLabel}</td>
                  <td>
                    <Dropdown
                      value={row.qbAccountId || null}
                      options={qbAccountOptions}
                      onChange={(e) => updateRow(row.channel, { qbAccountId: e.value })}
                      placeholder="Select QuickBooks account"
                      className="w-100"
                      filter
                      showClear
                    />
                    {rowErrors[row.channel] && (
                      <div className="text-danger small mt-1">{rowErrors[row.channel]}</div>
                    )}
                  </td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <button
                        className="btn btn-sm btn-outline-success"
                        title="Save"
                        disabled={savingRow === row.channel}
                        onClick={() => saveRow(row)}
                      >
                        {savingRow === row.channel ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="ti ti-device-floppy"></i>
                        )}
                      </button>
                      {row.status === "mapped" && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Remove mapping"
                          onClick={() => deleteRow(row)}
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── main modal ────────────────────────────────────────────────────────────────
const QuickBooksMappingModal = ({ visible, onHide, facilityId, qbConfigId, realmLabel }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Dialog
      header={`Map Accounts & Items${realmLabel ? ` — ${realmLabel}` : ""}`}
      visible={visible}
      onHide={onHide}
      style={{ width: "95vw", maxWidth: "1200px" }}
      maximizable
    >
      <ConfirmDialog />
      {!qbConfigId ? (
        <div className="alert alert-warning" role="alert">
          <i className="ti ti-alert-triangle me-2"></i>
          No QuickBooks connection selected.
        </div>
      ) : (
        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          <TabPanel header="Chart of Accounts">
            {visible && <ChartOfAccountsTab facilityId={facilityId} qbConfigId={qbConfigId} />}
          </TabPanel>
          <TabPanel header="Items">
            {visible && <ItemsTab facilityId={facilityId} qbConfigId={qbConfigId} />}
          </TabPanel>
          <TabPanel header="Payment Channels">
            {visible && <PaymentChannelsTab facilityId={facilityId} qbConfigId={qbConfigId} />}
          </TabPanel>
        </TabView>
      )}
    </Dialog>
  );
};

export default QuickBooksMappingModal;
